import React from 'react'
import { Tag, TagLabel, TagLeftIcon, Tooltip } from '@chakra-ui/react'
import { RiAlertLine } from 'react-icons/ri'
import { ClinicFieldsFragment } from '~graphql/generated/graphql'

/**
 * Minimal subset of `Clinic` fields required to determine whether the clinic
 * has finished every layer of initial sync it depends on. Accepting the
 * smallest viable shape (rather than the full `ClinicFieldsFragment`) lets
 * callers use this tag in lightweight list/header contexts that don't query
 * the full clinic fragment.
 */
export interface ClinicSyncStatusFields {
    last_sync_times?: Pick<
        NonNullable<ClinicFieldsFragment['last_sync_times']>,
        'last_full_sync'
    > | null
    pms_db?: {
        last_sync_times?: Pick<
            NonNullable<
                NonNullable<ClinicFieldsFragment['pms_db']>['last_sync_times']
            >,
            'last_full_sync'
        > | null
    } | null
}

/**
 * Returns true when the clinic is fully ready to serve data.
 *
 * Readiness depends on which sync layer actually owns the clinic's data:
 *   - When the clinic is backed by a shared `pms_db` (cloud PMSes), the data
 *     lives at the pms_db level, so readiness is determined solely by the
 *     pms_db's `last_full_sync`. The clinic-level `last_full_sync` is not
 *     meaningful in this case.
 *   - When there is no `pms_db`, the clinic owns its own data, so readiness
 *     is determined by the clinic's own `last_full_sync`.
 */
export function isClinicFullySynced(clinic: ClinicSyncStatusFields): boolean {
    if (clinic.pms_db) {
        return !!clinic.pms_db.last_sync_times?.last_full_sync
    }
    return !!clinic.last_sync_times?.last_full_sync
}

interface Props {
    clinic: ClinicSyncStatusFields
    size?: 'sm' | 'md'
}

/**
 * Small status pill that surfaces when a clinic has not yet completed every
 * required initial sync. Renders `null` when the clinic is fully synced so
 * it can be dropped inline next to clinic names/headers without taking up
 * space in the happy path.
 */
export default function NotFullySyncedTag({ clinic, size = 'sm' }: Props) {
    if (isClinicFullySynced(clinic)) return null

    return (
        <Tooltip
            label="Initial sync has not finished for this clinic. Some features may be unavailable until it completes."
            hasArrow
            placement="top"
        >
            <Tag
                colorScheme="yellow"
                size={size}
                rounded="full"
                px={2}
                py={1}
            >
                <TagLeftIcon as={RiAlertLine} boxSize="14px" />
                <TagLabel>Not Yet Fully Synced</TagLabel>
            </Tag>
        </Tooltip>
    )
}
